import * as React from "react";
import { Email, graphMailService } from "../../../services/graphMailService";
import { calendarService, type ContactTerminEvent } from "../../../services/calendarService";
import { calendarTerminService, type MailTerminDto } from "../../../services/calendarTerminService";
import { KalenderOverlay } from "../../calendar/KalenderOverlay";
import { useWindowManager } from "../../windowManager/WindowManagerContext";
import { fensterFuer, hatWeitereStufe, type KontaktFensterStufe } from "../../calendar/kontaktTerminFenster";
import { zeigeGlobalenHinweis } from "../../calendar/globalerHinweis";
import type { TerminAuswahl } from "../../calendar/calendarAuswahl";
import { mailLinkSnapshot } from "../mailLinkSnapshot";
import { effectiveHistoryAddresses } from "../mailContactHistory";
import {
    Abschnittstitel,
    Fehlerzeile,
    Hinweis,
    Laedt,
    Zeile,
    gestrichelterKnopfStyle,
    linkAktionStyle,
    titelZeileStyle,
} from "./sidebarChrome";
import { AUB, GOLD, INK2 } from "../../chat/chatTheme";
import { reportMailError } from "../../../services/mailError";
import { retryTerminStufe, terminBlockStatus } from "./termineLadezustand";
import { terminTeilnehmer } from "./terminTeilnehmer";
import { istAktuelleAnfrage } from "./requestGuard";

/**
 * Reiter „Termine" der rechten Mail-Leiste (J3, Cluster TERMINE).
 *
 * Dreigeteilt: (1) die mit DIESER Mail verknüpften Termine, (2) der
 * Verknüpfen-Knopf (öffnet den großen Kalender im Auswahlmodus), (3) alle
 * Termine mit dem Kontakt. Die drei haben eigene Zustände, damit ein Fehler in
 * einem Block die anderen nicht mitreißt.
 *
 * Diese Datei ist der Landeplatz von Cluster TERMINE: der Inhalt kam
 * unverändert aus `MailContactSidebar.tsx`, als die Leiste in Reiter-Bausteine
 * zerlegt wurde.
 */

const fmtDateTimeIso = (iso: string | null, isAllDay: boolean): string => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    if (isAllDay) return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
    return d.toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

export type TermineTabProps = {
    aktiv: boolean;
    contactId: number | null;
    contactName?: string | null;
    mailAddresses?: string[];
    mailbox: string | null;
    email: Email | null;
    /**
     * Kennung des Mail-FENSTERS, in dem diese Leiste steckt — `null` im
     * Lesepanel. Der Vollflächen-Kalender liegt bewusst auf der untersten
     * Sprosse über der Seite (siehe `AppFlaechenOverlay`), damit seine eigenen
     * Dialoge ohne z-index-Wettrüsten davor landen. Der Preis: der
     * Fenster-Layer (9000) liegt darüber. Statt die Leiter umzubauen, tritt
     * das Fenster für die Dauer der Auswahl beiseite.
     */
    fensterId?: string | null;
};

export const TermineTab: React.FC<TermineTabProps> = ({
    aktiv,
    contactId,
    contactName,
    mailAddresses,
    mailbox,
    email,
    fensterId,
}) => {
    const { minimizeWindow, restoreWindow } = useWindowManager();
    const [termine, setTermine] = React.useState<ContactTerminEvent[] | null>(null);
    const [verknuepfte, setVerknuepfte] = React.useState<MailTerminDto[] | null>(null);
    const [verknuepfteBusy, setVerknuepfteBusy] = React.useState<number | null>(null);
    const [fensterStufe, setFensterStufe] = React.useState<KontaktFensterStufe>(0);
    /** J3-2 — der große Kalender im Auswahlmodus (Vollflächen-Overlay, kein Fenster). */
    const [kalenderOffen, setKalenderOffen] = React.useState(false);
    // Alle je geöffneten Reiter bleiben montiert (die Leiste hält ihre Daten), und
    // das Overlay hängt per Portal am body — ohne diese Kopplung stünde der
    // Vollflächen-Kalender nach einem Reiterwechsel oder dem Schließen der Leiste
    // ohne Bezug weiter da. Erreichbar ist das aus einem Mail-Fenster heraus, das
    // über dem Overlay liegt und dessen Reiterzeile klickbar bleibt.
    React.useEffect(() => {
        if (!aktiv) setKalenderOffen(false);
    }, [aktiv]);

    // Solange der Vollflächen-Kalender offen ist, wandert das Mail-Fenster in
    // die Taskbar — sonst deckt es den Kalender zu (Fenster-Layer 9000 gegen
    // Overlay 1150). Als Effekt formuliert, damit JEDER Schließweg zurückholt:
    // Kreuz, „Fertig", ESC, Reiterwechsel, Mailwechsel und das Abräumen der
    // Leiste. Im Lesepanel ist `fensterId` null und nichts passiert.
    React.useEffect(() => {
        if (!fensterId || !kalenderOffen) return;
        minimizeWindow(fensterId);
        return () => restoreWindow(fensterId);
    }, [fensterId, kalenderOffen, minimizeWindow, restoreWindow]);
    const [vergangeneOffen, setVergangeneOffen] = React.useState(false);
    const [termineLaedt, setTermineLaedt] = React.useState(false);
    const [termineFehler, setTermineFehler] = React.useState<string | null>(null);
    const [termineFehlerStufe, setTermineFehlerStufe] = React.useState<KontaktFensterStufe | null>(null);
    const [verknuepfteLaedt, setVerknuepfteLaedt] = React.useState(false);
    const [verknuepfteFehler, setVerknuepfteFehler] = React.useState<string | null>(null);
    const [versuch, setVersuch] = React.useState(0);
    /**
     * J3/K8 — eigene Adressmenge NUR für den Termine-Block: mit
     * `beziehungen=1`, also Firma ↔ Ansprechpartner in beide Richtungen. Der
     * Schriftwechsel-Reiter behält bewusst seine engere Menge — sonst stünde
     * dort fremde Firmenpost.
     */
    const [terminAdressen, setTerminAdressen] = React.useState<string[] | null>(null);
    const [terminTeilnehmerAdressen, setTerminTeilnehmerAdressen] = React.useState<string[] | null>(null);
    const [terminTeilnehmerFehler, setTerminTeilnehmerFehler] = React.useState<string | null>(null);
    const [terminTeilnehmerVersuch, setTerminTeilnehmerVersuch] = React.useState(0);
    const verknuepfteAnfrageId = React.useRef(0);

    const messageId = email?.id ?? null;
    const conversationId = email?.conversationId ?? null;

    const adressKey = React.useMemo(
        () =>
            `${contactId ?? ""}|${[...(mailAddresses ?? [])]
                .map((a) => String(a ?? "").trim().toLowerCase())
                .filter(Boolean)
                .sort()
                .join(",")}`,
        [contactId, mailAddresses],
    );

    React.useEffect(() => {
        // Eine noch laufende Antwort der vorher geöffneten Mail darf die neue
        // Mail nicht überschreiben.
        verknuepfteAnfrageId.current += 1;
        setTermine(null);
        setVerknuepfte(null);
        setTerminAdressen(null);
        setTerminTeilnehmerAdressen(null);
        setTerminTeilnehmerFehler(null);
        setFensterStufe(0);
        setVergangeneOffen(false);
        setTermineFehler(null);
        setTermineFehlerStufe(null);
        setVerknuepfteFehler(null);
        setKalenderOffen(false);
    }, [adressKey, messageId]);

    React.useEffect(() => {
        if (!aktiv || contactId == null || terminTeilnehmerAdressen !== null) return;
        let abgebrochen = false;
        setTerminTeilnehmerFehler(null);
        void graphMailService.fetchContactTerminAttendeeAddresses(contactId)
            .then((adressen) => {
                if (!abgebrochen) setTerminTeilnehmerAdressen(adressen);
            })
            .catch((error) => {
                if (abgebrochen) return;
                // Der Absender bleibt als sinnvoller Fallback vorausgewählt. Der
                // unvollständige CRM-Abruf wird trotzdem sichtbar und zentral
                // geloggt, statt wie eine leere Adressmenge auszusehen.
                setTerminTeilnehmerAdressen([]);
                setTerminTeilnehmerFehler(reportMailError({
                    area: "mail-calendar",
                    action: "load-calendar",
                    error,
                    fallbackMessage: "Teilnehmer-Adressen konnten nicht vollständig geladen werden.",
                }));
            });
        return () => {
            abgebrochen = true;
        };
    }, [aktiv, contactId, terminTeilnehmerAdressen, terminTeilnehmerVersuch]);

    const ensureTerminAdressen = React.useCallback(async (): Promise<string[]> => {
        if (terminAdressen !== null) return terminAdressen;
        const contactAddrs =
            contactId != null
                ? await graphMailService.fetchContactMailAddresses(contactId, { beziehungen: true })
                : [];
        const eff = effectiveHistoryAddresses(contactAddrs, mailAddresses);
        setTerminAdressen(eff);
        return eff;
    }, [terminAdressen, contactId, mailAddresses]);

    /**
     * Alle Termine mit dem Kontakt im übergebenen Fenster. Mit KMVS-Kontakt über
     * `listContactTermine` (dort läuft die gemeinsame Adress-Auflösung
     * serverseitig), ohne Kontakt über die freie Adressliste.
     */
    const ladeKontaktTermine = React.useCallback(
        async (from: string, to: string): Promise<ContactTerminEvent[]> => {
            if (contactId != null) return calendarService.listContactTermine(contactId, from, to);
            const use = await ensureTerminAdressen();
            if (use.length === 0) return [];
            return calendarService.listCommonTermineByAddresses(use, from, to);
        },
        [contactId, ensureTerminAdressen],
    );

    /** Verknüpfte Termine neu lesen (nach Verknüpfen/Lösen). */
    const ladeVerknuepfte = React.useCallback(async () => {
        const anfrageId = ++verknuepfteAnfrageId.current;
        setVerknuepfteLaedt(true);
        setVerknuepfteFehler(null);
        if (!mailbox || !messageId) {
            if (istAktuelleAnfrage(anfrageId, verknuepfteAnfrageId.current)) {
                setVerknuepfte([]);
                setVerknuepfteLaedt(false);
            }
            return;
        }
        try {
            const geladen = await calendarTerminService.listTermineByMail(mailbox, messageId, conversationId);
            if (istAktuelleAnfrage(anfrageId, verknuepfteAnfrageId.current)) {
                setVerknuepfte(geladen);
            }
        } catch (error) {
            if (istAktuelleAnfrage(anfrageId, verknuepfteAnfrageId.current)) {
                setVerknuepfteFehler(
                    reportMailError({
                        area: "mail-calendar",
                        action: "load-calendar",
                        error,
                        fallbackMessage: "Verknüpfte Termine konnten nicht geladen werden.",
                    }),
                );
            }
        } finally {
            if (istAktuelleAnfrage(anfrageId, verknuepfteAnfrageId.current)) {
                setVerknuepfteLaedt(false);
            }
        }
    }, [mailbox, messageId, conversationId]);

    /** Diese Mail an einen Termin hängen (K1). */
    const verknuepfeMail = React.useCallback(
        async (t: { mailbox: string; graphEventId: string; subject?: string }) => {
            if (!mailbox || !email) return;
            try {
                await calendarTerminService.linkMailToTermin(
                    t.mailbox,
                    t.graphEventId,
                    mailLinkSnapshot(mailbox, email),
                );
                zeigeGlobalenHinweis(t.subject ? `Mit „${t.subject}" verknüpft.` : "Mit dem Termin verknüpft.");
                await ladeVerknuepfte();
            } catch (error) {
                // K3: sichtbar melden — der Klick kam womöglich aus dem
                // Kalender-FENSTER, diese Leiste ist dann gar nicht im Blick.
                zeigeGlobalenHinweis(
                    reportMailError({
                        area: "mail-calendar",
                        action: "link",
                        error,
                        fallbackMessage: "Mail konnte nicht mit dem Termin verknüpft werden.",
                    }),
                    { art: "fehler" },
                );
            }
        },
        [mailbox, email, ladeVerknuepfte],
    );

    /** Verknüpfung lösen — auch für im Kalender bereits gelöschte Termine. */
    const loeseVerknuepfung = React.useCallback(
        async (linkId: number) => {
            setVerknuepfteBusy(linkId);
            try {
                await calendarTerminService.unlinkMailFromTermin(linkId);
                await ladeVerknuepfte();
            } catch (error) {
                zeigeGlobalenHinweis(
                    reportMailError({
                        area: "mail-calendar",
                        action: "link",
                        error,
                        fallbackMessage: "Verknüpfung konnte nicht gelöst werden.",
                    }),
                    { art: "fehler" },
                );
            } finally {
                setVerknuepfteBusy(null);
            }
        },
        [ladeVerknuepfte],
    );

    /**
     * J3-2 — den großen Kalender im Auswahlmodus öffnen. Nicht mehr als Fenster:
     * der Kalender ist hier die Bühne und muss unter den Dialogen liegen, die er
     * selbst öffnet (siehe `KalenderOverlay`).
     */
    const openTerminKalender = React.useCallback(() => {
        if (!mailbox || !email) return;
        setKalenderOffen(true);
    }, [mailbox, email]);

    /** Häkchen im Kalender — folgt der Liste, nicht dem Stand beim Öffnen. */
    const verknuepfteRefs = React.useMemo(
        () =>
            (verknuepfte ?? []).map((t) => ({
                mailbox: t.mailbox,
                graphEventId: t.graphEventId,
            })),
        [verknuepfte],
    );

    /** Ein im Kalender neu angelegter Termin hängt danach automatisch an der Mail. */
    const neuTerminPrefill = React.useMemo(
        () =>
            mailbox && email
                ? {
                      linkMail: mailLinkSnapshot(mailbox, email),
                      contactId: contactId ?? null,
                      contactName: contactName ?? null,
                      initialAttendees: terminTeilnehmer(
                          terminTeilnehmerAdressen ?? [],
                          email.senderEmail,
                      ),
                  }
                : undefined,
        [mailbox, email, contactId, contactName, terminTeilnehmerAdressen],
    );

    /** Eine konkrete Zeitfenster-Stufe laden oder exakt wiederholen. */
    const ladeTerminStufe = React.useCallback(async (stufe: KontaktFensterStufe) => {
        setTermineLaedt(true);
        setTermineFehler(null);
        try {
            const { from, to } = fensterFuer(stufe);
            setTermine(await ladeKontaktTermine(from, to));
            setFensterStufe(stufe);
            setTermineFehlerStufe(null);
        } catch (error) {
            setTermineFehlerStufe(stufe);
            setTermineFehler(
                reportMailError({
                    area: "mail-calendar",
                    action: "load-calendar",
                    error,
                    fallbackMessage: "Termine konnten nicht geladen werden.",
                }),
            );
        } finally {
            setTermineLaedt(false);
        }
    }, [ladeKontaktTermine]);

    /** „Früher liegende Termine laden" (nächste Stufe des gemeinsamen Fensters). */
    const ladeFrueher = React.useCallback(() => {
        const naechste = (fensterStufe + 1) as KontaktFensterStufe;
        return ladeTerminStufe(naechste);
    }, [fensterStufe, ladeTerminStufe]);

    const wiederholeTerminLaden = React.useCallback(() => {
        const stufe = retryTerminStufe(termineFehlerStufe, fensterStufe);
        if (stufe === 0) {
            setTermineFehler(null);
            setVersuch((v) => v + 1);
            return;
        }
        void ladeTerminStufe(stufe);
    }, [termineFehlerStufe, fensterStufe, ladeTerminStufe]);

    React.useEffect(() => {
        if (!aktiv) return;
        void ladeVerknuepfte();
        let abgebrochen = false;
        const run = async () => {
            setTermineLaedt(true);
            setTermineFehler(null);
            setTermineFehlerStufe(null);
            try {
                // (3) alle Termine mit dem Kontakt — im gemeinsamen Fenster
                // (Vergangenheit UND Zukunft), identisch zum Vorgangs-Reiter.
                const { from, to } = fensterFuer(0);
                const list = await ladeKontaktTermine(from, to);
                if (!abgebrochen) {
                    setTermine(list);
                    setFensterStufe(0);
                }
            } catch (error) {
                const message = reportMailError({
                    area: "mail-calendar",
                    action: "load-calendar",
                    error,
                    fallbackMessage: "Termine konnten nicht geladen werden.",
                });
                if (!abgebrochen) {
                    setTermineFehlerStufe(0);
                    setTermineFehler(message);
                }
            } finally {
                if (!abgebrochen) setTermineLaedt(false);
            }
        };
        void run();
        return () => {
            abgebrochen = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aktiv, adressKey, messageId, versuch, ladeVerknuepfte, ladeKontaktTermine]);

    /** Eine Zeile der Kontakt-Terminliste (mit Vorgangs-Badge aus K9). */
    const TerminZeile: React.FC<{ termin: ContactTerminEvent }> = ({ termin: t }) => (
        <Zeile>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                    style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        flexShrink: 0,
                        backgroundColor: t.kmvs?.farbe ?? GOLD,
                    }}
                />
                <span style={titelZeileStyle}>{t.subject || "Termin"}</span>
            </div>
            <div style={{ fontSize: 12, color: INK2, paddingLeft: 16 }}>
                {fmtDateTimeIso(t.start?.dateTime ?? null, t.isAllDay) || "Zeit nicht verfügbar"}
            </div>
            {t.kmvs?.vorgangId != null && (
                <div style={{ fontSize: 11.5, color: AUB, paddingLeft: 16 }}>
                    {t.kmvs.vorgangTitel ?? `Vorgang #${t.kmvs.vorgangId}`}
                </div>
            )}
        </Zeile>
    );

    const verknuepfteStatus = terminBlockStatus(verknuepfte, verknuepfteLaedt, verknuepfteFehler);
    const termineStatus = terminBlockStatus(termine, termineLaedt, termineFehler);
    const links = verknuepfte ?? [];
    const alle = termine ?? [];
    const jetzt = Date.now();
    const startMs = (t: ContactTerminEvent): number => {
        const raw = t.start?.dateTime;
        if (!raw) return Number.NaN;
        return new Date(raw).getTime();
    };
    // Termine ohne bekannte Startzeit gelten als kommend — sie sollen nicht in
    // einem eingeklappten Vergangenheits-Block verschwinden.
    const kommend = alle
        .filter((t) => Number.isNaN(startMs(t)) || startMs(t) >= jetzt)
        .sort((a, b) => (startMs(a) || 0) - (startMs(b) || 0));
    const vergangen = alle
        .filter((t) => !Number.isNaN(startMs(t)) && startMs(t) < jetzt)
        .sort((a, b) => startMs(b) - startMs(a));

    const kontaktLabel = contactName?.trim() || "diesem Kontakt";

    return (
        <>
            {/* (1) Verknüpfte Termine — ganz oben. */}
            <Abschnittstitel>Verknüpfte Termine</Abschnittstitel>
            {verknuepfteStatus.kind === "loading" ? (
                <Laedt />
            ) : verknuepfteStatus.kind === "error" ? (
                <Fehlerzeile text={verknuepfteStatus.message} onRetry={() => void ladeVerknuepfte()} />
            ) : links.length === 0 ? (
                <Hinweis>Noch kein Termin mit dieser Mail verknüpft.</Hinweis>
            ) : (
                links.map((t) => (
                    <Zeile key={t.linkId}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span
                                style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    flexShrink: 0,
                                    backgroundColor: t.farbe ?? GOLD,
                                }}
                            />
                            <span style={titelZeileStyle}>
                                {t.status === "geloescht"
                                    ? "Termin wurde gelöscht"
                                    : t.status === "kein-zugriff"
                                      ? "Termin in einem Kalender ohne Leserecht"
                                      : t.subject || "Termin"}
                            </span>
                        </div>
                        <div style={{ fontSize: 12, color: INK2, paddingLeft: 16 }}>
                            {t.status === "ok"
                                ? fmtDateTimeIso(t.start, t.isAllDay) || "Zeit nicht verfügbar"
                                : t.status === "geloescht"
                                  ? "Der Termin existiert im Kalender nicht mehr."
                                  : "Betreff und Zeit sind nicht sichtbar."}
                        </div>
                        {t.vorgangId != null && (
                            <div style={{ fontSize: 11.5, color: AUB, paddingLeft: 16 }}>
                                {t.vorgangTitel ?? `Vorgang #${t.vorgangId}`}
                            </div>
                        )}
                        <button
                            type="button"
                            style={linkAktionStyle}
                            disabled={verknuepfteBusy === t.linkId}
                            onClick={() => void loeseVerknuepfung(t.linkId)}
                        >
                            {t.status === "geloescht" ? "Verknüpfung entfernen" : "Verknüpfung lösen"}
                        </button>
                    </Zeile>
                ))
            )}
            {(verknuepfteStatus.kind === "ready" || verknuepfteStatus.kind === "empty") &&
                verknuepfteStatus.error && (
                    <Fehlerzeile
                        text={verknuepfteStatus.error}
                        onRetry={() => void ladeVerknuepfte()}
                    />
                )}

            {/* (2) Verknüpfen — öffnet den großen Kalender im Auswahlmodus. */}
            {mailbox && email && (
                <button type="button" style={gestrichelterKnopfStyle} onClick={openTerminKalender}>
                    + Termin verknüpfen
                </button>
            )}
            {terminTeilnehmerFehler && (
                <Fehlerzeile
                    text={terminTeilnehmerFehler}
                    onRetry={() => {
                        setTerminTeilnehmerAdressen(null);
                        setTerminTeilnehmerFehler(null);
                        setTerminTeilnehmerVersuch((v) => v + 1);
                    }}
                />
            )}

            {/* (3) Alle Termine mit dem Kontakt — Vergangenheit UND Zukunft. */}
            <Abschnittstitel>Alle Termine mit {kontaktLabel}</Abschnittstitel>
            {termineStatus.kind === "loading" ? (
                <Laedt />
            ) : termineStatus.kind === "error" ? (
                <Fehlerzeile
                    text={termineStatus.message}
                    onRetry={wiederholeTerminLaden}
                />
            ) : alle.length === 0 ? (
                <Hinweis>Keine gemeinsamen Termine im Zeitraum.</Hinweis>
            ) : (
                <>
                    {kommend.map((t) => (
                        <TerminZeile key={`k:${t.source.address}:${t.id}`} termin={t} />
                    ))}
                    {vergangen.length > 0 && (
                        <details
                            className="cal-liste-vergangen"
                            open={vergangeneOffen}
                            onToggle={(e) => setVergangeneOffen((e.target as HTMLDetailsElement).open)}
                        >
                            <summary>Vergangene ({vergangen.length})</summary>
                            {vergangen.map((t) => (
                                <TerminZeile key={`v:${t.source.address}:${t.id}`} termin={t} />
                            ))}
                        </details>
                    )}
                </>
            )}
            {termineFehler && termine !== null && (
                <Fehlerzeile text={termineFehler} onRetry={wiederholeTerminLaden} />
            )}
            {hatWeitereStufe(fensterStufe) && termineStatus.kind !== "loading" && (
                <button
                    type="button"
                    style={linkAktionStyle}
                    disabled={termineLaedt}
                    onClick={() => void ladeFrueher()}
                >
                    {termineLaedt ? "Termine werden geladen…" : "Früher liegende Termine laden"}
                </button>
            )}

            {/* Der Kalender füllt die App-Fläche; geschlossen wird über „Fertig",
                das Kreuz oder ESC. */}
            {kalenderOffen && mailbox && email && (
                <KalenderOverlay
                    titel="Termin mit dieser Mail verknüpfen"
                    bereitsVerknuepft={verknuepfteRefs}
                    neuTerminPrefill={neuTerminPrefill}
                    onPick={(t: TerminAuswahl) => verknuepfeMail(t)}
                    onSchliessen={() => setKalenderOffen(false)}
                />
            )}
        </>
    );
};
